export const getOnboardingProgress = (application = {}) => {
    const completed = [
        Boolean(application.assignedTeacherId),
        Boolean(application.biographyMeetingScheduledAt),
        Boolean(application.biographyPaymentCompletedAt),
        Boolean(application.biographyMeetingCompletedAt),
    ];
    const activeStepIndex = completed.findIndex((value) => !value);

    return {
        completed,
        activeStepIndex,
        isComplete: activeStepIndex === -1,
    };
};

/** Approved applications count as done for the final step, in the list as well as in the drawer. */
export const isApplicationApproved = (application = {}) =>
    ["approved", "completed"].includes(String(application.status || "").toLowerCase());

/**
 * The one step order for processing an application, shared by the list and the detail drawer:
 * 1 handler, 2 plan meeting, 3 payment, 4 biography meeting held, 5 approved.
 */
export const APPLICATION_STEP_KEYS = Object.freeze([
    "HANDLER",
    "BIOGRAPHY_MEETING_SCHEDULED",
    "BIOGRAPHY_PAYMENT",
    "BIOGRAPHY_MEETING",
    "APPROVED",
]);

export const getApplicationStepCompletion = (application = {}) => ({
    HANDLER: Boolean(application.assignedTeacherId),
    BIOGRAPHY_MEETING_SCHEDULED: Boolean(application.biographyMeetingScheduledAt),
    BIOGRAPHY_PAYMENT: Boolean(application.biographyPaymentCompletedAt),
    BIOGRAPHY_MEETING: Boolean(application.biographyMeetingCompletedAt),
    APPROVED: isApplicationApproved(application),
});

/**
 * Why a completed step can no longer be undone, mirroring the backend workflow rules
 * (FamilyApplicationServiceImpl.updateWorkflowStep); null when undoing is allowed.
 */
export const getUndoBlockReason = (application = {}, stepKey) => {
    if (stepKey === "BIOGRAPHY_PAYMENT" && application.biographyMeetingCompletedAt) return "meeting_completed";
    if (stepKey === "BIOGRAPHY_MEETING" && isApplicationApproved(application)) return "approved";
    return null;
};

export const getChildAge = (dateBirth, today = new Date()) => {
    if (!dateBirth) return null;
    const parts = Array.isArray(dateBirth)
        ? dateBirth
        : String(dateBirth).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)?.slice(1).map(Number);
    const birth = parts?.length >= 3
        ? new Date(parts[0], parts[1] - 1, parts[2], 12)
        : new Date(dateBirth);
    if (Number.isNaN(birth.getTime())) return null;
    let age = today.getFullYear() - birth.getFullYear();
    if (today.getMonth() < birth.getMonth()
        || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age -= 1;
    return age;
};

export const getClassroomPlacementCandidates = (children = [], today = new Date()) =>
    children
        .map((child, index) => ({
            child,
            index,
            age: getChildAge(child?.dateBirth || child?.dateOfBirth || child?.birthDate, today),
        }))
        // Children that already exist in Lumen with a current classroom don't need a direct placement.
        .filter(({ child, age }) => age !== null && age >= 7 && !child?.currentClassroomId);

const localDate = (value) => {
    if (!value) return null;
    const parts = Array.isArray(value)
        ? value
        : String(value).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)?.slice(1).map(Number);
    const date = parts?.length >= 3
        ? new Date(parts[0], parts[1] - 1, parts[2])
        : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const schoolYearId = (value) => value?.id ?? value?.uuid ?? value?.schoolYearId
    ?? value?.school_year_id ?? value?.schoolYearUuid ?? null;

const classroomSchoolYearId = (classroom) => classroom?.schoolYearId
    ?? classroom?.school_year_id ?? classroom?.schoolYear?.id
    ?? classroom?.schoolYear?.uuid ?? classroom?.schoolYear?.schoolYearId ?? null;

export const isCurrentSchoolYear = (schoolYear, today = new Date()) => {
    const start = localDate(schoolYear?.startDate ?? schoolYear?.start_date);
    const end = localDate(schoolYear?.endDate ?? schoolYear?.end_date);
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (start && end) return start <= day && day <= end;
    return schoolYear?.current === true || schoolYear?.isCurrent === true
        || schoolYear?.active === true || schoolYear?.isActive === true
        || schoolYear?.currentSchoolYear === true
        || String(schoolYear?.status || "").toUpperCase() === "ACTIVE";
};

export const filterClassroomsForCurrentSchoolYear = (classrooms = [], schoolYears = [], today = new Date()) => {
    const currentYear = schoolYears.find((year) => isCurrentSchoolYear(year, today));
    const currentYearId = schoolYearId(currentYear);
    if (currentYearId != null) {
        const currentStart = localDate(currentYear?.startDate ?? currentYear?.start_date)?.getTime();
        const currentEnd = localDate(currentYear?.endDate ?? currentYear?.end_date)?.getTime();
        return classrooms.filter((classroom) => {
            const linkedId = classroomSchoolYearId(classroom);
            if (linkedId != null) return String(linkedId) === String(currentYearId);
            const linkedYear = classroom?.schoolYear || classroom;
            return localDate(linkedYear?.startDate ?? linkedYear?.start_date)?.getTime() === currentStart
                && localDate(linkedYear?.endDate ?? linkedYear?.end_date)?.getTime() === currentEnd;
        });
    }
    return classrooms.filter((classroom) => isCurrentSchoolYear(classroom?.schoolYear || classroom, today));
};

export const getSchoolYearLabel = (classroom) => {
    const year = classroom?.schoolYear || {};
    const direct = classroom?.schoolYearLabel || classroom?.schoolYearName
        || classroom?.yearLabel || year?.label || year?.name;
    if (direct) return String(direct);
    const start = localDate(year?.startDate ?? classroom?.startDate);
    const end = localDate(year?.endDate ?? classroom?.endDate);
    return start && end ? `${start.getFullYear()}/${end.getFullYear()}` : "";
};

const hasExplicitOffset = (value) =>
    typeof value === "string" && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);

export const toAmsterdamDateTimeInputValue = (value) => {
    if (!value) return "";
    const text = String(value);
    if (!hasExplicitOffset(text)) return text.slice(0, 16);
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return "";
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Amsterdam",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).formatToParts(date);
    const part = (type) => parts.find((item) => item.type === type)?.value || "";
    return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
};
